import {
	UserOutlined,
	BankOutlined,
	ClusterOutlined,
	TeamOutlined,
	CalendarOutlined,
	ClockCircleOutlined,
	BarChartOutlined,
	SafetyCertificateOutlined,
} from '@ant-design/icons'
import type { WelcomeLayout, WelcomeModuleDef, WelcomeModuleIcon } from './WelcomeCustomizeDrawer'
import organization from '../../Assets/img/organization.png'
import adminUser from '../../Assets/img/admin-user.png'
import department from '../../Assets/img/department.png'
import checkout from '../../Assets/img/check-out.png'
import attendance from '../../Assets/img/attendance.png'
import staffShift from '../../Assets/img/staff-shift.png'
import privacy from '../../Assets/img/privacy.png'
import leaves from '../../Assets/img/leaves.png'

export const MODULE_DEFS: WelcomeModuleDef[] = [
	{ key: 'admin-users', defaultLabel: 'Admin Users', defaultIcon: UserOutlined, defaultImg: adminUser },
	{ key: 'organization', defaultLabel: 'Organization', defaultIcon: BankOutlined, defaultImg: organization },
	{ key: 'department', defaultLabel: 'Roles & Departments', defaultIcon: ClusterOutlined, defaultImg: department },
	{ key: 'staffs', defaultLabel: 'Staffs', defaultIcon: TeamOutlined, defaultImg: staffShift },
	{ key: 'leaves', defaultLabel: 'Leaves', defaultIcon: CalendarOutlined, defaultImg: leaves },
	{ key: 'checkin-checkout', defaultLabel: 'Checkin / Checkout', defaultIcon: ClockCircleOutlined, defaultImg: checkout },
	{ key: 'attendance', defaultLabel: 'Attendance', defaultIcon: BarChartOutlined, defaultImg: attendance },
	{ key: 'privacy-policy', defaultLabel: 'Privacy Policy', defaultIcon: SafetyCertificateOutlined, defaultImg: privacy },
]

/** React Router paths for each welcome-module key */
export const MODULE_ROUTES: Record<string, string> = {
	'admin-users': '/admin-user',
	organization: '/organization',
	department: '/department',
	staffs: '/staff/list',
	leaves: '/leaves',
	'checkin-checkout': '/checkin-checkout',
	attendance: '/attendance',
	'privacy-policy': '/PrivacyPolicy',
}

/** First module opens by default when using sidebar layout */
export const DEFAULT_SIDEBAR_ENTRY_PATH = MODULE_ROUTES['admin-users']

/** Persists layout so the sidebar shell shows immediately after navigation (no fetch race). */
export const WELCOME_LAYOUT_STORAGE_KEY = 'edc-portal-welcome-layout'
export const WELCOME_MODULE_ICONS_STORAGE_KEY = 'edc-portal-welcome-module-icons'

export const WELCOME_LAYOUT_CHANGE_EVENT = 'edc-welcome-layout-change'

export function normalizeWelcomeLayout(v: unknown): WelcomeLayout {
	return v === 'sidebar' ? 'sidebar' : 'tiles'
}

export function readStoredWelcomeLayout(): WelcomeLayout {
	try {
		const v = localStorage.getItem(WELCOME_LAYOUT_STORAGE_KEY)
		if (v === 'sidebar' || v === 'tiles') return v
	} catch {
		/* ignore */
	}
	return 'tiles'
}

export function persistWelcomeLayout(layout: WelcomeLayout) {
	try {
		localStorage.setItem(WELCOME_LAYOUT_STORAGE_KEY, layout)
	} catch {
		/* ignore */
	}
}

function normalizeModuleIcons(
	value: unknown,
): Record<string, WelcomeModuleIcon> {
	if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
	const raw = value as Record<string, unknown>
	const next: Record<string, WelcomeModuleIcon> = {}
	Object.entries(raw).forEach(([k, v]) => {
		if (!v || typeof v !== 'object' || Array.isArray(v)) return
		const icon = v as Record<string, unknown>
		const type = icon.type
		const iconValue = icon.value
		if (
			(type === 'icon' || type === 'image') &&
			typeof iconValue === 'string' &&
			iconValue.trim().length > 0
		) {
			next[k] = {
				type,
				value: iconValue,
				storagePath:
					typeof icon.storagePath === 'string' ? icon.storagePath : null,
			}
		}
	})
	return next
}

export function readStoredWelcomeModuleIcons(): Record<string, WelcomeModuleIcon> {
	try {
		const raw = localStorage.getItem(WELCOME_MODULE_ICONS_STORAGE_KEY)
		if (!raw) return {}
		return normalizeModuleIcons(JSON.parse(raw))
	} catch {
		return {}
	}
}

export function persistWelcomeModuleIcons(
	icons: Record<string, WelcomeModuleIcon>,
) {
	try {
		localStorage.setItem(
			WELCOME_MODULE_ICONS_STORAGE_KEY,
			JSON.stringify(normalizeModuleIcons(icons)),
		)
	} catch {
		/* ignore */
	}
}

function hasExplicitStoredLayout(): boolean {
	try {
		return localStorage.getItem(WELCOME_LAYOUT_STORAGE_KEY) != null
	} catch {
		return false
	}
}

/**
 * Prefer the last explicit client choice when it disagrees with GET (PUT can lag).
 * If nothing was stored yet, trust the server.
 */
export function mergeWelcomeLayoutFromServer(serverValue: unknown): WelcomeLayout {
	const server = normalizeWelcomeLayout(serverValue)
	if (!hasExplicitStoredLayout()) return server
	const stored = readStoredWelcomeLayout()
	if (stored !== server) return stored
	return server
}

export function broadcastWelcomeLayout(layout: WelcomeLayout) {
	if (typeof window === 'undefined') return
	window.dispatchEvent(
		new CustomEvent<WelcomeLayout>(WELCOME_LAYOUT_CHANGE_EVENT, { detail: layout }),
	)
}

/** Unwraps GET/PUT response: envelope is `{ data: settings }` or already the settings object. */
export function getAppSettingsPayload(res: unknown): Record<string, unknown> {
	if (res == null || typeof res !== 'object') return {}
	const obj = res as Record<string, unknown>
	const inner = obj.data
	if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
		return inner as Record<string, unknown>
	}
	return obj
}

/**
 * Map current location to the sidebar module key (for Menu selectedKeys).
 */
export function pathToModuleKey(pathname: string): string {
	const p = pathname.toLowerCase()
	if (pathname === '/welcome') return ''
	if (pathname === '/admin-user') return 'admin-users'
	if (pathname === '/organization') return 'organization'
	if (pathname === '/department' || pathname === '/role') return 'department'
	if (pathname === '/staff_bulk_upload' || pathname.startsWith('/staff')) return 'staffs'
	if (pathname.startsWith('/leaves')) return 'leaves'
	if (pathname === '/checkin-checkout') return 'checkin-checkout'
	if (pathname === '/attendance' || pathname.startsWith('/attendance/')) return 'attendance'
	if (pathname === '/PrivacyPolicy' || p === '/privacypolicy') return 'privacy-policy'
	return 'admin-users'
}

/** Whether the welcome module sidebar should wrap this URL (flat routes; not layout nesting). */
export function sidebarModulePathMatches(pathname: string): boolean {
	const p = pathname.toLowerCase()
	if (pathname === '/welcome') return true
	if (pathname === '/admin-user') return true
	if (pathname === '/organization') return true
	if (pathname === '/department' || pathname === '/role') return true
	if (pathname === '/staff_bulk_upload' || pathname.startsWith('/staff')) return true
	if (pathname.startsWith('/leaves')) return true
	if (pathname === '/checkin-checkout') return true
	if (pathname === '/attendance' || pathname.startsWith('/attendance/')) return true
	if (pathname === '/PrivacyPolicy') return true
	if (p === '/privacypolicy') return false
	return false
}
